import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import NuevoTrabajador from "@/pages/NuevoTrabajador";
import EditarTrabajador from "@/pages/EditarTrabajador";
import Expediente from "@/pages/Expediente";
import GestionUsuarios from "@/pages/GestionUsuarios";
import BandejaEntrada from "@/pages/BandejaEntrada";
import HistorialCambios from "@/pages/HistorialCambios";
import GestionEmpresas from "@/pages/GestionEmpresas";
import Administrativo from "@/pages/Administrativo";
import Produccion from "@/pages/Produccion";
import ProduccionNew from "@/pages/ProduccionNew";
import GestionFinca from "@/pages/GestionFinca";
import Maquinaria from "@/pages/Maquinaria";
import MaquinariaInspecciones from "@/pages/MaquinariaInspecciones";
import MaquinariaUso from "@/pages/MaquinariaUso";
import ExpedienteVehiculo from "@/pages/ExpedienteVehiculo";
import Ganado from "@/pages/Ganado";
import ControlAsistencia from "@/pages/ControlAsistencia";
import HistorialDisciplinario from "@/pages/HistorialDisciplinario";
import SaludSeguridadProduccion from "@/pages/SaludSeguridadProduccion";
import RegistroLabores from "@/pages/RegistroLabores";
import Planilla from "@/pages/Planilla";
import ReportesProduccion from "@/pages/ReportesProduccion";
import HojaPesador from "@/pages/HojaPesador";
import TableroTareas from "@/pages/TableroTareasCompleto";
import CronogramaCosecha from "@/pages/CronogramaCosecha";
import Mantenimiento from "@/pages/Mantenimiento";
import RegistroPicudos from "@/pages/RegistroPicudos";
import ActasVisitas from "@/pages/ActasVisitas";
import ActaPublica from "@/pages/ActaPublica";
import Configuracion from "@/pages/Configuracion";
import TopLevelLayout from "@/components/TopLevelLayout";
import MainLayout from "@/components/MainLayout";
import { Toaster } from "@/components/ui/sonner";
import ServiceWorkerUpdater from "@/components/ServiceWorkerUpdater";
import PreviewBanner from "@/components/PreviewBanner";

// Ganadería Module Imports
import GanaderiaLayout from "@/components/GanaderiaLayout";
import GanadoDashboard from "@/pages/GanadoDashboard";
import CattleRegistry from "@/pages/CattleRegistry";
import WeightTracking from "@/pages/WeightTracking";
import HealthManagement from "@/pages/HealthManagement";
import CattleInventory from "@/pages/CattleInventory";
import CattleDetail from "@/pages/CattleDetail";
import ReproductionManagement from "@/pages/ReproductionManagement";
import { CattleProvider } from "@/contexts/CattleContext";
import { HealthProvider } from "@/contexts/HealthContext";
import { ReproductionProvider } from "@/contexts/ReproductionContext";
import { useInitialRoute, ProtectedRoute } from "@/components/ProtectedRoute";
import NoAccess from "@/pages/NoAccess";

function App() {
  // Initialize user state from localStorage using lazy initialization
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  });
  const [loading, setLoading] = useState(false);
  
  // Get initial route based on user permissions
  const initialRoute = useInitialRoute(user);

  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="text-emerald-800 text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <PreviewBanner />
      <ServiceWorkerUpdater />
      <BrowserRouter>
        <Routes>
          <Route 
            path="/login" 
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to={initialRoute} />} 
          />
          {/* Public acta view via QR — no auth required */}
          <Route path="/acta/:id" element={<ActaPublica />} />
          <Route 
            path="/" 
            element={user ? <Navigate to={initialRoute} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/no-access" 
            element={user ? <NoAccess onLogout={handleLogout} /> : <Navigate to="/login" />} 
          />
          
          {/* Ganadería Module - Top Level Routes with nested tabs */}
          <Route 
            path="/ganaderia/*" 
            element={
              user ? (
                <CattleProvider>
                  <HealthProvider>
                    <ReproductionProvider>
                      <TopLevelLayout user={user} onLogout={handleLogout} activeTab="ganaderia">
                        <GanaderiaLayout>
                          <Routes>
                            <Route path="/" element={<GanadoDashboard />} />
                            <Route path="/registro" element={<CattleRegistry />} />
                            <Route path="/peso" element={<WeightTracking />} />
                            <Route path="/salud" element={<HealthManagement />} />
                            <Route path="/inventario" element={<CattleInventory />} />
                            <Route path="/reproduccion" element={<ReproductionManagement />} />
                            <Route path="/detalle/:id" element={<CattleDetail />} />
                          </Routes>
                        </GanaderiaLayout>
                      </TopLevelLayout>
                    </ReproductionProvider>
                  </HealthProvider>
                </CattleProvider>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          
          {/* Agricultura Routes - Con MainLayout */}
          <Route 
            path="/administrativo" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <Administrativo user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/rrhh" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <Dashboard user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/rrhh/asistencia" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <ControlAsistencia user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/rrhh/historial-disciplinario" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <HistorialDisciplinario user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <ProduccionNew user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/maquinaria" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <Maquinaria user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/maquinaria/inspecciones" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <MaquinariaInspecciones user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/maquinaria/uso" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <MaquinariaUso user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/expediente-vehiculo/:vehiculoId" 
            element={
              user ? (
                <ExpedienteVehiculo user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          
          {/* Producción Sub-tabs */}
          <Route 
            path="/produccion/salud-seguridad" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <SaludSeguridadProduccion user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion/registros" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <RegistroLabores user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion/planilla" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <Planilla user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion/reportes" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <ReportesProduccion user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion/hoja-pesador" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <HojaPesador user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion/tablero-tareas" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <TableroTareas user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion/cronograma" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <CronogramaCosecha user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion/actas-visitas" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <ActasVisitas user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion/mantenimiento" 
            element={
              user ? (
                <ProtectedRoute user={user} requiredPermission="agricultura.produccion.mantenimiento.view">
                  <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                    <MainLayout user={user} onLogout={handleLogout}>
                      <Mantenimiento user={user} onLogout={handleLogout} />
                    </MainLayout>
                  </TopLevelLayout>
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/produccion/mantenimiento/picudos" 
            element={
              user ? (
                <ProtectedRoute user={user} requiredPermission="agricultura.produccion.mantenimiento.view">
                  <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                    <MainLayout user={user} onLogout={handleLogout}>
                      <RegistroPicudos user={user} onLogout={handleLogout} />
                    </MainLayout>
                  </TopLevelLayout>
                </ProtectedRoute>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          
          {/* Worker Management Routes */}
          <Route 
            path="/trabajadores/nuevo" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <NuevoTrabajador user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/trabajadores/:id/editar" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <EditarTrabajador user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/trabajadores/:id/expediente" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <Expediente user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          
          {/* Admin and Utility Routes */}
          <Route 
            path="/usuarios" 
            element={
              user && user.is_admin ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <GestionUsuarios user={user} onLogout={handleLogout} updateUser={updateUser} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/rrhh" />
              )
            } 
          />
          <Route 
            path="/bandeja" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <BandejaEntrada user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/historial" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <HistorialCambios user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/empresas" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <MainLayout user={user} onLogout={handleLogout}>
                    <GestionEmpresas user={user} onLogout={handleLogout} />
                  </MainLayout>
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/configuracion" 
            element={
              user ? (
                <TopLevelLayout user={user} onLogout={handleLogout} activeTab="agricultura">
                  <Configuracion user={user} />
                </TopLevelLayout>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
